/**
 * CVR Gift Wrap Selector
 * カートにギフトラッピング商品が入っている時、お会計ボタン押下で
 * 「どの商品をラッピングするか」を選択するモーダルを表示。
 * 選択された商品の line property `_wrap=true` をセットしてから /checkout へ。
 */
(function () {
  'use strict';

  const MODAL_ID = 'CvrGiftWrapModal';

  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  function getGiftWrapId() {
    const m = getModal();
    return m ? m.dataset.cvrGwId : null;
  }

  // Cached state — synchronously available to click interceptor
  let cvrGwInCart = false;
  let cvrClearingProps = false;

  function clearAllWrapProperties(cart) {
    if (cvrClearingProps) return Promise.resolve();
    const targets = cart.items.filter(
      (it) => it.properties && it.properties['ラッピング対象']
    );
    if (targets.length === 0) return Promise.resolve();
    cvrClearingProps = true;
    let p = Promise.resolve();
    targets.forEach((it) => {
      p = p.then(() =>
        fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: it.key, properties: {} })
        }).catch(() => null)
      );
    });
    return p.finally(() => { cvrClearingProps = false; });
  }

  function refreshGwState() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((cart) => {
        const gwId = getGiftWrapId();
        const wasInCart = cvrGwInCart;
        if (!gwId) { cvrGwInCart = false; }
        else {
          cvrGwInCart = cart.items.some(
            (it) => String(it.variant_id) === String(gwId) || String(it.id) === String(gwId)
          );
        }
        // If gift wrap was removed → clear all line properties
        if (wasInCart && !cvrGwInCart) {
          return clearAllWrapProperties(cart).then(() => cvrGwInCart);
        }
        return cvrGwInCart;
      })
      .catch(() => { cvrGwInCart = false; return false; });
  }

  function openModal() {
    const m = getModal();
    if (!m) return;
    m.hidden = false;
    document.body.style.overflow = 'hidden';
    // focus first checkbox or close
    const first = m.querySelector('[data-cvr-gw-confirm]');
    if (first) first.focus();
  }

  function closeModal() {
    const m = getModal();
    if (!m) return;
    m.hidden = true;
    document.body.style.overflow = '';
  }

  function setLoading(on) {
    const m = getModal();
    if (!m) return;
    const el = m.querySelector('[data-cvr-gw-loading]');
    const confirm = m.querySelector('[data-cvr-gw-confirm]');
    if (el) el.hidden = !on;
    if (confirm) confirm.disabled = !!on;
  }

  function updateLineProperty(lineKey, wantWrap) {
    // Use /cart/change.js with id (line key) and properties.
    // For unchecked items send properties:{} to fully clear (Shopify replaces line properties).
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        properties: wantWrap ? { 'ラッピング対象': 'はい' } : {}
      })
    }).then((r) => r.json());
  }

  function onConfirm() {
    const m = getModal();
    if (!m) return;
    const anyChecked = !!m.querySelector('[data-line-key]:checked');
    if (!anyChecked) return; // safeguard: button should already be disabled
    setLoading(true);
    const checks = Array.from(m.querySelectorAll('[data-line-key]'));

    // Sequentially update each line (Shopify cart endpoints can race)
    const tasks = checks.map((cb) => () => updateLineProperty(cb.dataset.lineKey, cb.checked));
    let p = Promise.resolve();
    tasks.forEach((t) => { p = p.then(t).catch(() => null); });

    p.catch(() => null).then(() => {
      window.location.href = '/checkout';
    });
  }

  function bindModal() {
    const m = getModal();
    if (!m || m.dataset.cvrGwBound === '1') return;
    m.dataset.cvrGwBound = '1';
    m.querySelectorAll('[data-cvr-gw-close]').forEach((el) => {
      el.addEventListener('click', closeModal);
    });
    const confirmBtn = m.querySelector('[data-cvr-gw-confirm]');
    if (confirmBtn) confirmBtn.addEventListener('click', onConfirm);
    const selectAll = m.querySelector('[data-cvr-gw-select-all]');
    if (selectAll) selectAll.addEventListener('click', () => {
      m.querySelectorAll('[data-line-key]').forEach((cb) => { cb.checked = true; });
      updateConfirmEnabled();
    });
    const clearAll = m.querySelector('[data-cvr-gw-clear-all]');
    if (clearAll) clearAll.addEventListener('click', () => {
      m.querySelectorAll('[data-line-key]').forEach((cb) => { cb.checked = false; });
      updateConfirmEnabled();
    });
    m.querySelectorAll('[data-line-key]').forEach((cb) => {
      cb.addEventListener('change', updateConfirmEnabled);
    });
    updateConfirmEnabled();
    // ESC to close
    document.addEventListener('keydown', (e) => {
      if (!m.hidden && e.key === 'Escape') closeModal();
    });
  }

  function interceptCheckoutClick(e) {
    if (!getModal()) return;
    if (!cvrGwInCart) return; // No gift wrap — let native checkout proceed
    const btn = e.target.closest('[name="checkout"], #checkout, .cart__checkout-button, [data-checkout-button]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    openModal();
  }

  function updateConfirmEnabled() {
    const m = getModal();
    if (!m) return;
    const confirmBtn = m.querySelector('[data-cvr-gw-confirm]');
    if (!confirmBtn) return;
    const anyChecked = !!m.querySelector('[data-line-key]:checked');
    confirmBtn.disabled = !anyChecked;
    confirmBtn.classList.toggle('cvr-gw-modal__confirm--disabled', !anyChecked);
  }

  function initialCleanupIfOrphaned() {
    // On page load, if there are line properties but no gift wrap in cart, clear them
    fetch('/cart.js', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((cart) => {
        const gwId = getGiftWrapId();
        const gwPresent = gwId && cart.items.some(
          (it) => String(it.variant_id) === String(gwId) || String(it.id) === String(gwId)
        );
        if (!gwPresent) clearAllWrapProperties(cart);
      })
      .catch(() => null);
  }

  function init() {
    bindModal();
    refreshGwState();
    initialCleanupIfOrphaned();
    document.addEventListener('click', interceptCheckoutClick, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function rebindAndRefresh(cart) {
    bindModal();
    // If event payload contains cart, update cache synchronously
    if (cart && Array.isArray(cart.items)) {
      const gwId = getGiftWrapId();
      const wasInCart = cvrGwInCart;
      cvrGwInCart = gwId
        ? cart.items.some((it) => String(it.variant_id) === String(gwId) || String(it.id) === String(gwId))
        : false;
      if (wasInCart && !cvrGwInCart) clearAllWrapProperties(cart);
      return;
    }
    refreshGwState();
  }
  document.addEventListener('cart:updated', rebindAndRefresh);
  document.addEventListener('cart:refresh', rebindAndRefresh);
  document.addEventListener('cart-updated', rebindAndRefresh);

  // Foxtheme internal pub/sub — the gift-wrapping toggle uses this
  function subscribeFoxThemeEvents() {
    if (!window.FoxThemeEvents || typeof window.FoxThemeEvents.subscribe !== 'function') return false;
    window.FoxThemeEvents.subscribe('cart-update', (cart) => rebindAndRefresh(cart));
    return true;
  }
  if (!subscribeFoxThemeEvents()) {
    // Retry until Foxtheme global is ready
    let tries = 0;
    const iv = setInterval(() => {
      if (subscribeFoxThemeEvents() || ++tries > 20) clearInterval(iv);
    }, 200);
  }
})();
